/* eslint-disable @typescript-eslint/no-use-before-define */
import { ListingLabelContext } from 'contexts';
import { find, trim } from 'lodash';
import React, { useContext, useMemo, useState } from 'react';
import AppointmentBookingInternalContext from '../AppointmentBookingInternalContext';
import AgentOverrideOverlay from './AgentOverrideOverlay';
import { SHOWING_SCREEN_DECISION_TREE } from './constants';
import NoMoreShowingsOverlay from './NoMoreShowingsOverlay';
import { ShowingScreenType } from './types';

const NoShowingsWrapper: React.FC = ({ children }) => {
  const { listing, partialAppointmentConfig, overrideShowingType } = useContext(
    AppointmentBookingInternalContext,
  );
  const { getAvailability } = useContext(ListingLabelContext);
  const [overrideNoMoreShowings, setOverrideNoMoreShowings] = useState(false);

  const showingScreenType = useMemo(() => {
    if (!listing || !partialAppointmentConfig) return ShowingScreenType.Default;
    // follow the path in the tree until a decision(leaf) is found.
    const decide = (
      tree: typeof SHOWING_SCREEN_DECISION_TREE,
      path: string[],
    ): ShowingScreenType => {
      if (typeof tree !== 'object') return tree;
      const [key, ...rest] = path;
      const nextBranch =
        tree[key] ||
        find(tree, (_, k) => k.split('|').map(trim).includes(key)) ||
        tree.default;
      return decide(nextBranch, rest);
    };

    const path = [
      listing.canEdit ? 'canEdit' : 'default',
      listing.availability,
      partialAppointmentConfig.noMoreShowings ? 'noMoreShowings' : 'default',
    ];
    return decide(SHOWING_SCREEN_DECISION_TREE, path);
  }, [listing, partialAppointmentConfig]);

  if (
    overrideShowingType ||
    overrideNoMoreShowings ||
    !listing ||
    !partialAppointmentConfig
  ) {
    return <>{children}</>;
  }

  const listingStatus = getAvailability(listing.availability);

  switch (showingScreenType) {
    case ShowingScreenType.Forbid:
    case ShowingScreenType.AdminOverride:
      return (
        <NoMoreShowingsOverlay
          allowOverride={showingScreenType === ShowingScreenType.AdminOverride}
          listingId={listing.id}
          listingStatus={listingStatus}
          onOverride={() => setOverrideNoMoreShowings(true)}
        />
      );
    case ShowingScreenType.AgentOverride:
      return <AgentOverrideOverlay listingStatus={listingStatus} />;
    default:
      return <>{children}</>;
  }
};

export default NoShowingsWrapper;
